import { defineStore } from "pinia";
import { ref } from "vue";
import { listBoardSprints } from "@/api/boards";
import type { ApiError, SprintListItem } from "@/api/types";
import { useApi } from "@/composables/useApi";
import {
  buildNarrative,
  cadenceSummary,
  deriveProjectHealth,
  metricSpark,
  scanRisks,
  HEADLINE_METRIC_IDS,
} from "@/lib/insights";

interface BoardEntry {
  sprints: SprintListItem[];
  boardName: string | null;
  loadedAt: number;
  error: ApiError | null;
}

/**
 * Board-keyed scorecard data. Distinct from the project store: a board can span
 * multiple projects (a shared board's sprint fans out to one row per project),
 * so every sprint row here carries its own `project_key`.
 */
export const useBoardsStore = defineStore("boards", () => {
  const byBoard = ref<Record<string, BoardEntry>>({});
  const loadingKey = ref<string | null>(null);

  function entry(boardId: string): BoardEntry {
    return byBoard.value[boardId] ?? { sprints: [], boardName: null, loadedAt: 0, error: null };
  }

  async function load(boardId: string | number, force = false): Promise<void> {
    const key = String(boardId);
    const cur = entry(key);
    if (!force && Date.now() - cur.loadedAt < 60_000 && cur.sprints.length > 0) return;
    const { client } = useApi();
    loadingKey.value = key;
    try {
      const res = await listBoardSprints(client, key);
      byBoard.value = {
        ...byBoard.value,
        [key]: { sprints: res.sprints, boardName: res.board_name, loadedAt: Date.now(), error: null },
      };
    } catch (err) {
      byBoard.value = {
        ...byBoard.value,
        [key]: { ...cur, error: err as ApiError },
      };
    } finally {
      if (loadingKey.value === key) loadingKey.value = null;
    }
  }

  function items(boardId: string | number): SprintListItem[] {
    return entry(String(boardId)).sprints;
  }
  function boardName(boardId: string | number): string | null {
    return entry(String(boardId)).boardName;
  }
  // A board is one type; read it off any row (board endpoint carries board_type).
  function boardType(boardId: string | number): "scrum" | "kanban" {
    const first = entry(String(boardId)).sprints[0];
    return (first?.board_type ?? "scrum") === "kanban" ? "kanban" : "scrum";
  }
  // Distinct projects this board touches (≥2 ⇒ shared board).
  function projectKeys(boardId: string | number): string[] {
    const seen = new Set<string>();
    for (const s of entry(String(boardId)).sprints) {
      if (s.project_key) seen.add(s.project_key);
    }
    return [...seen].sort();
  }
  function latest(boardId: string | number): SprintListItem | undefined {
    return entry(String(boardId)).sprints[0];
  }
  function error(boardId: string | number): ApiError | null {
    return entry(String(boardId)).error;
  }
  function loading(boardId: string | number): boolean {
    return loadingKey.value === String(boardId);
  }

  // Insights over a single-project board's sprint list (dedup not needed when
  // every sprint maps to one row). Callers pass an already project-scoped list.
  function insightsFor(list: SprintListItem[]) {
    const risks = scanRisks(list);
    return {
      health: deriveProjectHealth(list),
      cadence: cadenceSummary(list),
      risks,
      narrative: buildNarrative(list, risks),
      headlines: HEADLINE_METRIC_IDS.map((id) => metricSpark(list, id, 6)),
    };
  }

  return {
    byBoard,
    loadingKey,
    load,
    items,
    boardName,
    boardType,
    projectKeys,
    latest,
    error,
    loading,
    insightsFor,
  };
});
